import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Repeat, CheckCircle, Loader2, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { thumb } from "../lib/img";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubCard = ({ s, onCancel, busy }) => (
  <div className="bg-white rounded-2xl border border-cream-dark p-6 text-left" data-testid="subscription-card">
    <div className="flex gap-4">
      <img src={thumb(s.product_image, 300) || "/product-placeholder.svg"} alt="" className="w-24 h-24 rounded-xl object-cover" />
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-widest text-[#B85C38]">Fiori freschi a casa</p>
        <h2 className="font-serif text-xl text-forest leading-tight">{s.product_name}</h2>
        <p className="text-sm text-sage">{s.shop_name} · {s.interval_label} · €{s.unit_price.toFixed(2)} a consegna</p>
        <p className="text-sm text-sage">{s.delivery_address}, {s.delivery_city}</p>
      </div>
    </div>
    <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
      <div className="bg-cream rounded-lg p-3"><p className="text-xs text-sage">Stato</p><p className="font-medium text-forest" data-testid="subscription-status">{s.status === "active" ? "Attivo" : s.status === "cancelled" ? "Sospeso" : "In attivazione"}</p></div>
      <div className="bg-cream rounded-lg p-3"><p className="text-xs text-sage">Prossima consegna</p><p className="font-medium text-forest">{s.status === "active" ? (s.next_delivery || "—") : "—"}</p></div>
    </div>
    <p className="text-xs text-sage mt-3">Consegne effettuate: {s.deliveries_count}</p>
    {s.status === "active" && onCancel && <Button variant="outline" onClick={onCancel} disabled={busy} className="mt-4 w-full border-red-200 text-red-700 hover:bg-red-50" data-testid="subscription-cancel">Sospendi l'abbonamento</Button>}
  </div>
);

export function SubscriptionSuccessPage() {
  const [params] = useSearchParams();
  const [sub, setSub] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setError("Sessione non trovata"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/per-me/subscriptions/status/${sid}`);
        if (data.status === "active" || tries > 6) setSub(data); else { tries += 1; setTimeout(poll, 2000); }
      } catch (e) { setError(e.response?.data?.detail || "Impossibile verificare l'abbonamento"); }
    };
    poll();
  }, [params]);
  return (
    <div className="min-h-[70vh] bg-cream flex items-center justify-center px-6 py-16" data-testid="subscription-success-page">
      <div className="max-w-md w-full text-center">
        {error ? <><XCircle className="h-12 w-12 text-red-500 mx-auto mb-3" /><p className="text-forest">{error}</p></> : !sub ? <><Loader2 className="h-12 w-12 text-forest mx-auto mb-3 animate-spin" /><p className="text-sage">Stiamo attivando il tuo abbonamento…</p></> : (
          <>
            <CheckCircle className="h-14 w-14 text-emerald-600 mx-auto mb-3" />
            <h1 className="font-serif text-3xl text-forest mb-2" data-testid="subscription-success-title">{sub.status === "active" ? "Abbonamento attivo!" : "Quasi fatto"}</h1>
            <p className="text-sage mb-6">{sub.status === "active" ? "La prima consegna è già in preparazione. Ti abbiamo inviato un'email con il link per gestirlo." : "Il pagamento è in verifica: riceverai un'email appena attivo."}</p>
            <SubCard s={sub} />
            <Link to="/fiori-per-me" className="inline-block mt-6 text-brand underline underline-offset-2">Torna a Fiori per me</Link>
          </>
        )}
      </div>
    </div>
  );
}

export default function SubscriptionManagePage() {
  const { subId } = useParams();
  const [params] = useSearchParams();
  const t = params.get("t") || "";
  const [sub, setSub] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/per-me/subscriptions/${subId}`, { params: { t } }).then(r => setSub(r.data)).catch(e => setError(e.response?.data?.detail || "Link non valido")); }, [subId, t]);
  const cancel = async () => {
    if (!window.confirm("Sospendere l'abbonamento? Non riceverai altre consegne né addebiti.")) return;
    setBusy(true);
    try { setSub((await axios.post(`${API}/per-me/subscriptions/${subId}/cancel?t=${t}`)).data); } catch (e) { setError(e.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-[70vh] bg-cream flex items-center justify-center px-6 py-16" data-testid="subscription-manage-page">
      <div className="max-w-md w-full text-center">
        <Repeat className="h-10 w-10 text-[#B85C38] mx-auto mb-3" />
        <h1 className="font-serif text-3xl text-forest mb-6">Il tuo abbonamento</h1>
        {error ? <p className="text-forest">{error}</p> : !sub ? <p className="text-sage">Caricamento…</p> : <SubCard s={sub} onCancel={cancel} busy={busy} />}
        {sub?.status === "cancelled" && <p className="text-sm text-sage mt-4">Abbonamento sospeso. Quando vorrai, potrai attivarne uno nuovo da <Link to="/fiori-per-me" className="underline">Fiori per me</Link>.</p>}
      </div>
    </div>
  );
}
